import type { Peer } from "@/api/generated/model";
import { useEffect, useMemo, useState } from "react";
import { Menu } from "lucide-react";
import { useParams } from "react-router-dom";
import { useListPeers } from "@/api/generated/enrollments/enrollments";
import { LoadingState } from "@/components/common/LoadingState";
import { ErrorState } from "@/components/common/ErrorState";
import TrainerCourseTree from "@/components/course/tree/TrainerCourseTree";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useEnrollment } from "@/hooks/useEnrollment";
import {
  ParticipantsOverviewTable,
  type ParticipantRow,
} from "@/components/dashboard/ParticipantsOverviewTable";
import { ParticipantDetailsPanel } from "@/components/dashboard/ParticipantDetailsPanel";
import { GroupOverviewPanel } from "@/components/dashboard/GroupOverviewPanel";
import { groupAverageProgress } from "@/components/dashboard/group-overview/aggregateModuleStats";

const peerAverageProgress = (peer: Peer): number => {
  if (peer.modules.length === 0) return 0;
  return Math.round(
    peer.modules.reduce(
      (sum, moduleProgress) => sum + moduleProgress.progressPercent,
      0
    ) / peer.modules.length
  );
};

const mapPeerToParticipantRow = (peer: Peer, index: number): ParticipantRow => {
  const lastActivityAt = peer.lastActivityAt
    ? new Date(peer.lastActivityAt)
    : null;

  const currentModuleTitle = peer.currentModuleTitle ?? null;
  const currentModuleProgressPercent = currentModuleTitle
    ? (peer.modules.find(
        (moduleProgress) => moduleProgress.title === currentModuleTitle
      )?.progressPercent ?? null)
    : null;

  const overallProgressPercent = peerAverageProgress(peer);

  return {
    userId: peer.userId,
    enrollmentId: peer.id,
    firstName: peer.firstName ?? "",
    lastName: peer.lastNameInitial ?? "",
    colorIndex: index % 10,
    lastActivityAt,
    overallProgressPercent,
    currentModuleTitle,
    currentModuleProgressPercent,
  };
};

export const DashboardTrainer = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();

  const [selectedRow, setSelectedRow] = useState<ParticipantRow | null>(null);

  const fetchTrainerPreviewCourse = useCourseDetailStore(
    (state) => state.fetchTrainerPreviewCourse
  );

  useEffect(() => {
    if (enrollmentId) fetchTrainerPreviewCourse(enrollmentId);
  }, [enrollmentId, fetchTrainerPreviewCourse]);

  const {
    data: enrollment,
    error: enrollmentError,
    isLoading: enrollmentLoading,
  } = useEnrollment(enrollmentId);

  const showCourseTitleInHeader = () => {
    if (enrollment)
      useCourseDetailStore.setState({ courseTitle: enrollment.courseTitle });
  };
  useEffect(showCourseTitleInHeader, [enrollment]);

  const {
    data: peersData,
    error: peersError,
    isLoading: peersLoading,
  } = useListPeers<Error>(enrollmentId ?? "", {
    swr: { enabled: Boolean(enrollmentId) },
  });
  const peers = peersData?.peers ?? null;

  const loading = enrollmentLoading || peersLoading;
  const error = enrollmentError?.message ?? peersError?.message;

  const groupAverage = useMemo(
    () => groupAverageProgress(peers ?? []),
    [peers]
  );

  const participants = useMemo(
    () => (peers ?? []).map(mapPeerToParticipantRow),
    [peers]
  );

  if (!enrollmentId) {
    return (
      <ErrorState
        title="Dashboard konnte nicht geladen werden"
        message="Keine Einschreibungs-ID in der URL"
      />
    );
  }

  if (loading) return <LoadingState message="Dashboard wird geladen..." />;
  if (error)
    return (
      <ErrorState
        title="Dashboard konnte nicht geladen werden"
        message={error}
      />
    );

  const renderSidePanel = () => {
    if (!selectedRow) {
      return (
        <GroupOverviewPanel
          peers={peers ?? []}
          participants={participants}
          onSelectParticipant={setSelectedRow}
        />
      );
    }

    return (
      <ParticipantDetailsPanel
        participantEnrollmentId={selectedRow.enrollmentId}
        colorIndex={selectedRow.colorIndex}
        currentModuleTitle={selectedRow.currentModuleTitle}
        groupAveragePercent={groupAverage}
        onClose={() => setSelectedRow(null)}
      />
    );
  };

  return (
    <div className="flex min-h-full flex-col lg:h-full lg:min-h-0 lg:overflow-hidden">
      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-4 px-8 py-4 lg:min-h-0">
        <div className="flex shrink-0 justify-end">
          <Sheet>
            <SheetTrigger asChild>
              <Button type="button" size="sm">
                <Menu className="size-4" />
                Kursstruktur öffnen
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-lg p-0 sm:max-w-lg">
              <SheetHeader className="sr-only">
                <SheetTitle>Kursstruktur</SheetTitle>
              </SheetHeader>
              <div className="h-full overflow-hidden">
                <TrainerCourseTree />
              </div>
            </SheetContent>
          </Sheet>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-5 lg:grid-rows-[minmax(0,1fr)]">
          <div className="min-w-0 lg:col-span-3 lg:min-h-0">
            <ParticipantsOverviewTable
              participants={participants}
              selectedUserId={selectedRow?.userId ?? null}
              onRowClick={setSelectedRow}
            />
          </div>
          <div className="flex flex-col gap-4 lg:col-span-2 lg:min-h-0">
            {/* TODO: alerts panel ("Unterstützung empfohlen") can be added here. */}
            {renderSidePanel()}
          </div>
        </div>
      </div>
    </div>
  );
};
